import { Box, Chip, IconButton, Paper, Typography, useTheme } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuActivity, LuBox, LuBuilding2, LuChevronLeft, LuChevronRight, LuLayers, LuTrendingUp } from "react-icons/lu";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart as RePieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import muiStyle from "../../../style/components-style/muiStyle";

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatNumber = (num) => {
  if (!num) return "0";
  if (num >= 1000) return (num / 1000).toFixed(1) + "k";
  return num.toFixed(1);
};

const COLORS = ["#16a34a", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"];

// ============================================================================
// BUILDING SPOTLIGHT COMPONENT (Portfolio Overview + Individual Building)
// ============================================================================

const BuildingSpotlight = ({ buildings, aggregatedData, currentIndex, onNext, onPrev }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Create "all buildings" overview item
  const allItems = useMemo(() => {
    const overview = { id: "portfolio-overview", name: "Buildings Overview" };
    return [overview, ...buildings];
  }, [buildings]);

  const currentItem = allItems[currentIndex] || allItems[0];
  const isOverview = currentItem.id === "portfolio-overview";

  // Calculate stats
  const totalGwp = aggregatedData?.totalGwp || 0;
  const buildingCount = buildings.length;
  const avgGwp = buildingCount > 0 ? totalGwp / buildingCount : 0;

  if (isOverview) {
    // Portfolio Overview - Professional Cards with proper text colors
    const topEmitter = buildings.length > 0 ? buildings.reduce((max, b) => (b.gwp > max.gwp ? b : max), buildings[0]) : null;

    return (
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`, borderRadius: 3, bgcolor: isDark ? "#1c1c1e" : "white" }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box>
            <Typography sx={{ ...muiStyle.getTableTitle(isDark), fontSize: 20, color: isDark ? "#4ade80" : muiStyle.typographyTableTitle.color }}>
              {t("Buildings Overview")}
            </Typography>
            <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 13, color: isDark ? "#94a3b8" : undefined }}>{t("Aggregated metrics across all buildings")}</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton
              onClick={onPrev}
              size="small"
              sx={{ bgcolor: isDark ? "#27272a" : "#f8fafc", "&:hover": { bgcolor: isDark ? "#3f3f46" : "#e2e8f0" }, color: isDark ? "#e4e4e7" : "inherit" }}>
              <LuChevronLeft size={20} />
            </IconButton>
            <IconButton
              onClick={onNext}
              size="small"
              sx={{ bgcolor: isDark ? "#27272a" : "#f8fafc", "&:hover": { bgcolor: isDark ? "#3f3f46" : "#e2e8f0" }, color: isDark ? "#e4e4e7" : "inherit" }}>
              <LuChevronRight size={20} />
            </IconButton>
          </Box>
        </Box>

        {/* Stats Cards - Premium Modern Cards */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr 1fr" }, gap: 3 }}>
          {/* Total Emissions card */}
          <Box
            sx={{
              p: 3,
              borderRadius: 5,
              bgcolor: isDark ? "#27272a" : "white",
              border: `1px solid ${isDark ? "#3f3f46" : "#f1f5f9"}`,
              boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,0,0,0.03)",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              position: "relative",
              overflow: "hidden",
            }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 3,
                bgcolor: isDark ? "rgba(22, 163, 74, 0.2)" : "#f0fdf4",
                color: isDark ? "#4ade80" : "#16a34a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 10px rgba(22, 163, 74, 0.1)",
              }}>
              <LuTrendingUp size={22} />
            </Box>
            <Box>
              <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 13, color: isDark ? "#94a3b8" : "#64748b", fontWeight: 600 }}>Total Emissions</Typography>
              <Typography sx={{ fontFamily: "Poppins", fontSize: 28, fontWeight: 800, color: isDark ? "#f1f5f9" : "#1e293b", lineHeight: 1.2, mt: 0.5 }}>
                {formatNumber(totalGwp / 1000)}
                <span style={{ fontSize: 14, fontWeight: 500, color: "#94a3b8", marginLeft: 4 }}>tCO₂e</span>
              </Typography>
            </Box>
            {/* Decorative background element */}
            <Box sx={{ position: "absolute", right: -15, bottom: -15, opacity: 0.03, transform: "rotate(-15deg)" }}>
              <LuTrendingUp size={100} />
            </Box>
          </Box>

          {/* Building Count card */}
          <Box
            sx={{
              p: 3,
              borderRadius: 5,
              bgcolor: isDark ? "#27272a" : "white",
              border: `1px solid ${isDark ? "#3f3f46" : "#f1f5f9"}`,
              boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,0,0,0.03)",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              position: "relative",
              overflow: "hidden",
            }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 3,
                bgcolor: isDark ? "rgba(59, 130, 246, 0.2)" : "#eff6ff",
                color: isDark ? "#60a5fa" : "#3b82f6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 10px rgba(59, 130, 246, 0.1)",
              }}>
              <LuBuilding2 size={22} />
            </Box>
            <Box>
              <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 13, color: isDark ? "#94a3b8" : "#64748b", fontWeight: 600 }}>Portfolio Size</Typography>
              <Typography sx={{ fontFamily: "Poppins", fontSize: 28, fontWeight: 800, color: isDark ? "#f1f5f9" : "#1e293b", lineHeight: 1.2, mt: 0.5 }}>
                {buildingCount}
                <span style={{ fontSize: 14, fontWeight: 500, color: "#94a3b8", marginLeft: 4 }}>Buildings</span>
              </Typography>
            </Box>
            <Box sx={{ position: "absolute", right: -15, bottom: -15, opacity: 0.03, transform: "rotate(-15deg)" }}>
              <LuBuilding2 size={100} />
            </Box>
          </Box>

          {/* Average per Building card */}
          <Box
            sx={{
              p: 3,
              borderRadius: 5,
              bgcolor: isDark ? "#27272a" : "white",
              border: `1px solid ${isDark ? "#3f3f46" : "#f1f5f9"}`,
              boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,0,0,0.03)",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              position: "relative",
              overflow: "hidden",
            }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 3,
                bgcolor: isDark ? "rgba(245, 158, 11, 0.2)" : "#fff7ed",
                color: isDark ? "#fbbf24" : "#f59e0b",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 10px rgba(245, 158, 11, 0.1)",
              }}>
              <LuActivity size={22} />
            </Box>
            <Box>
              <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 13, color: isDark ? "#94a3b8" : "#64748b", fontWeight: 600 }}>Average Intensity</Typography>
              <Typography sx={{ fontFamily: "Poppins", fontSize: 28, fontWeight: 800, color: isDark ? "#f1f5f9" : "#1e293b", lineHeight: 1.2, mt: 0.5 }}>
                {formatNumber(avgGwp / 1000)}
                <span style={{ fontSize: 14, fontWeight: 500, color: "#94a3b8", marginLeft: 4 }}>t/Bldg</span>
              </Typography>
            </Box>
            <Box sx={{ position: "absolute", right: -15, bottom: -15, opacity: 0.03, transform: "rotate(-15deg)" }}>
              <LuActivity size={100} />
            </Box>
          </Box>

          {/* Top Emitter card */}
          {topEmitter && (
            <Box
              sx={{
                p: 3,
                borderRadius: 5,
                bgcolor: isDark ? "#27272a" : "white",
                border: `1px solid ${isDark ? "#3f3f46" : "#f1f5f9"}`,
                boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,0,0,0.03)",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                position: "relative",
                overflow: "hidden",
              }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 3,
                  bgcolor: isDark ? "rgba(139, 92, 246, 0.2)" : "#f5f3ff",
                  color: isDark ? "#a78bfa" : "#8b5cf6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 10px rgba(139, 92, 246, 0.1)",
                }}>
                <LuLayers size={22} />
              </Box>
              <Box>
                <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 13, color: isDark ? "#94a3b8" : "#64748b", fontWeight: 600 }}>Highest Footprint</Typography>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: 16,
                    fontWeight: 700,
                    color: isDark ? "#f1f5f9" : "#1e293b",
                    lineHeight: 1.2,
                    mt: 0.5,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                  {topEmitter.name}
                </Typography>
                <Typography sx={{ fontFamily: "Poppins", fontSize: 13, color: isDark ? "#a78bfa" : "#8b5cf6", fontWeight: 600, mt: 0.5 }}>
                  {formatNumber(topEmitter.gwp / 1000)} tCO₂e
                </Typography>
              </Box>
              <Box sx={{ position: "absolute", right: -15, bottom: -15, opacity: 0.03, transform: "rotate(-15deg)" }}>
                <LuLayers size={100} />
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    );
  }

  // Individual Building View
  const building = currentItem;
  const diff = building.gwp - avgGwp;
  const diffPercent = avgGwp > 0 ? Math.abs((diff / avgGwp) * 100) : 0;
  const isBetter = diff < 0;

  return (
    <Paper elevation={0} sx={{ p: 3, mb: 3, border: "1px solid #e2e8f0", borderRadius: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ ...muiStyle.typographyTableTitle, fontSize: 20 }}>{building.name}</Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
            {building.building_category && <Chip label={building.building_category} size="small" sx={{ bgcolor: "#f1f5f9", ...muiStyle.typographyPageSubtitle, fontSize: 12 }} />}
            {building.location && <Chip label={building.location} size="small" sx={{ bgcolor: "#f1f5f9", ...muiStyle.typographyPageSubtitle, fontSize: 12 }} />}
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton onClick={onPrev} size="small" sx={{ bgcolor: "#f8fafc", "&:hover": { bgcolor: "#e2e8f0" } }}>
            <LuChevronLeft size={20} />
          </IconButton>
          <IconButton onClick={onNext} size="small" sx={{ bgcolor: "#f8fafc", "&:hover": { bgcolor: "#e2e8f0" } }}>
            <LuChevronRight size={20} />
          </IconButton>
        </Box>
      </Box>

      {/* Stats Row */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {/* Emissions */}
        <Box
          sx={{
            flex: 1,
            minWidth: 180,
            p: 2.5,
            background: isBetter ? "linear-gradient(135deg, #16a34a 0%, #15803d 100%)" : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
            borderRadius: 3,
          }}>
          <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 13, color: "rgba(255,255,255,0.9)", mb: 1 }}>Tons CO₂e</Typography>
          <Typography sx={{ fontFamily: "Poppins", fontSize: 32, fontWeight: 700, lineHeight: 1, color: "white" }}>{formatNumber(building.gwp / 1000)}</Typography>
          <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 12, color: "rgba(255,255,255,0.9)", mt: 1 }}>
            {isBetter ? "↓" : "↑"} {diffPercent.toFixed(0)}% vs avg
          </Typography>
        </Box>

        {/* Year Built */}
        {building.year && (
          <Box
            sx={{
              flex: 1,
              minWidth: 180,
              p: 2.5,
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 3,
            }}>
            <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 13, mb: 1 }}>Year Built</Typography>
            <Typography sx={{ ...muiStyle.typographyTableTitle, fontSize: 32, lineHeight: 1 }}>{building.year}</Typography>
          </Box>
        )}

        {/* Square Footage */}
        {building.gross_floor_area && (
          <Box
            sx={{
              flex: 1,
              minWidth: 180,
              p: 2.5,
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 3,
            }}>
            <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 13, mb: 1 }}>Sq. Ft</Typography>
            <Typography sx={{ ...muiStyle.typographyTableTitle, fontSize: 32, lineHeight: 1 }}>{building.gross_floor_area.toLocaleString()}</Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

// ============================================================================
// LIFE CYCLE STAGES COMPONENT - WITH BOTH CARDS AND CHART
// ============================================================================

const LifeCycleStages = ({ stageData }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [viewMode, setViewMode] = useState("cards");
  const total = useMemo(() => stageData.reduce((sum, s) => sum + s.value, 0), [stageData]);

  return (
    <Paper elevation={0} sx={{ p: 3, mb: 3, border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`, borderRadius: 3, bgcolor: isDark ? "#1c1c1e" : "white" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
        <Box>
          <Typography sx={{ ...muiStyle.getTableTitle(isDark), fontSize: 18, color: isDark ? "#4ade80" : muiStyle.typographyTableTitle.color }}>
            {t("Life Cycle Stages")}
          </Typography>
          <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 13, color: isDark ? "#94a3b8" : undefined }}>
            {t("Carbon emissions breakdown by life cycle phase")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => setViewMode("cards")}
            sx={{
              bgcolor: viewMode === "cards" ? "#16a34a" : isDark ? "#27272a" : "transparent",
              color: viewMode === "cards" ? "white" : isDark ? "#e4e4e7" : "#94a3b8",
              border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`,
              "&:hover": { bgcolor: viewMode === "cards" ? "#15803d" : isDark ? "#3f3f46" : "#f8fafc" },
            }}>
            <LuBox size={16} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setViewMode("chart")}
            sx={{
              bgcolor: viewMode === "chart" ? "#16a34a" : isDark ? "#27272a" : "transparent",
              color: viewMode === "chart" ? "white" : isDark ? "#e4e4e7" : "#94a3b8",
              border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`,
              "&:hover": { bgcolor: viewMode === "chart" ? "#15803d" : isDark ? "#3f3f46" : "#f8fafc" },
            }}>
            <LuActivity size={16} />
          </IconButton>
        </Box>
      </Box>

      {viewMode === "cards" ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
          {stageData.map((stage, idx) => {
            const percent = total > 0 ? (stage.value / total) * 100 : 0;
            return (
              <Box
                key={idx}
                sx={{
                  p: 3,
                  bgcolor: isDark ? stage.color || "#27272a" : "white",
                  borderRadius: 4,
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.4)" : "0 2px 10px rgba(0,0,0,0.04)",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#f1f5f9"}`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: isDark ? "0 8px 30px rgba(0,0,0,0.5)" : "0 8px 25px rgba(0,0,0,0.06)",
                    transform: "translateY(-2px)",
                  },
                }}>
                {/* Decorative Accent Bar */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    bgcolor: stage.color,
                    borderRadius: "4px 0 0 4px",
                  }}
                />

                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: "Poppins",
                          fontSize: "12px",
                          fontWeight: 700,
                          color: stage.color,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                          mb: 0.5,
                        }}>
                        {stage.title}
                      </Typography>
                      <Typography
                        sx={{
                          ...muiStyle.typographyTableTitle,
                          fontSize: 18,
                          color: isDark ? "#ffffff" : "#1e293b",
                        }}>
                        {stage.subtitle}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography
                        sx={{
                          fontFamily: "Poppins",
                          fontSize: 22,
                          fontWeight: 800,
                          color: isDark ? "#ffffff" : "#1e293b",
                          lineHeight: 1,
                        }}>
                        {formatNumber(stage.value)}
                        <span style={{ fontSize: 13, fontWeight: 500, color: isDark ? "rgba(255,255,255,0.7)" : "#64748b", marginLeft: 4 }}>t</span>
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Poppins",
                          fontSize: 13,
                          fontWeight: 600,
                          color: stage.color,
                          mt: 0.5,
                        }}>
                        {percent.toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* Progress Bar Container */}
                  <Box
                    sx={{
                      width: "100%",
                      height: 8,
                      bgcolor: "#f1f5f9",
                      borderRadius: 4,
                      overflow: "hidden",
                      position: "relative",
                    }}>
                    <Box
                      sx={{
                        width: `${percent}%`,
                        height: "100%",
                        bgcolor: stage.color,
                        borderRadius: 4,
                        transition: "width 1s cubic-bezier(0.4, 0, 0.2, 1)",
                        boxShadow: `0 0 10px ${stage.color}40`,
                        backgroundImage: "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0) 100%)",
                        backgroundSize: "200% 100%",
                        animation: "shimmer 3s infinite linear",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box sx={{ height: 400 }}>
          <ResponsiveContainer>
            <BarChart data={stageData} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="title" tick={{ fontSize: 12, fill: isDark ? '#ffffff' : '#333333', fontFamily: "Poppins, sans-serif", fontWeight: 600 }} />
              <YAxis
                tick={{ fontSize: 12, fill: isDark ? '#ffffff' : '#333333', fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
                label={{ value: "Tons CO₂e", angle: -90, position: "insideLeft", style: { fontFamily: "Poppins, sans-serif", fontSize: 12, fontWeight: 600 } }}
              />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", fontFamily: "Poppins" }}
                formatter={(value) => [`${formatNumber(value)} t`, "Emissions"]}
              />
              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                {stageData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
};

// ============================================================================
// MATERIAL COMPOSITION COMPONENT
// ============================================================================

const MaterialComposition = ({ materialData }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [viewMode, setViewMode] = useState("chart");

  const totalValue = useMemo(() => materialData.reduce((sum, d) => sum + d.value, 0), [materialData]);

  const dataWithPercent = useMemo(
    () =>
      materialData.map((d) => ({
        ...d,
        percent: totalValue > 0 ? d.value / totalValue : 0,
      })),
    [materialData, totalValue]
  );

  return (
    <Paper elevation={0} sx={{ p: 3, mb: 3, border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`, borderRadius: 4, bgcolor: isDark ? "#1c1c1e" : "white" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
        <Box>
          <Typography sx={{ ...muiStyle.getTableTitle(isDark), fontSize: 18, color: isDark ? "#4ade80" : muiStyle.typographyTableTitle.color }}>
            {t("Material Composition")}
          </Typography>
          <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 13, color: isDark ? "#94a3b8" : undefined }}>
            {t("Carbon footprint breakdown by material type")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => setViewMode("chart")}
            sx={{
              bgcolor: viewMode === "chart" ? "#16a34a" : isDark ? "#27272a" : "transparent",
              color: viewMode === "chart" ? "white" : isDark ? "#e4e4e7" : "#94a3b8",
              border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`,
              borderRadius: "8px",
              "&:hover": { bgcolor: viewMode === "chart" ? "#15803d" : isDark ? "#3f3f46" : "#f8fafc" },
            }}>
            <LuActivity size={16} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setViewMode("table")}
            sx={{
              bgcolor: viewMode === "table" ? "#16a34a" : isDark ? "#27272a" : "transparent",
              color: viewMode === "table" ? "white" : isDark ? "#e4e4e7" : "#94a3b8",
              border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`,
              borderRadius: "8px",
              "&:hover": { bgcolor: viewMode === "table" ? "#15803d" : isDark ? "#3f3f46" : "#f8fafc" },
            }}>
            <LuBox size={16} />
          </IconButton>
        </Box>
      </Box>

      {/* Content: Doughnut Chart (Cards mode) or Table (Table mode) */}
      <Box sx={{ minHeight: 400 }}>
        {viewMode === "chart" ? (
          <Box sx={{ position: "relative", width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Box sx={{ height: 400, width: "100%" }}>
              <ResponsiveContainer>
                <RePieChart>
                  <Pie data={dataWithPercent} dataKey="value" nameKey="label" cx="50%" cy="50%" innerRadius={100} outerRadius={140} paddingAngle={3}>
                    {dataWithPercent.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="rgba(255,255,255,0.2)" />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => `${formatNumber(value)} t (${((value / totalValue) * 100).toFixed(1)}%)`}
                    contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 8px 30px rgba(0,0,0,0.1)", fontFamily: "Poppins" }}
                  />
                  <Legend verticalAlign="bottom" align="center" iconType="circle" wrapperStyle={{ fontFamily: "Poppins", fontSize: 13, paddingTop: "20px" }} />
                </RePieChart>
              </ResponsiveContainer>
            </Box>
            {/* Center Label for Doughnut - Perfectly Centered */}
            <Box
              sx={{
                position: "absolute",
                top: "41%", // Adjusted for legend at bottom
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                pointerEvents: "none",
              }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#64748b", fontFamily: "Poppins", mb: -0.5 }}>Total</Typography>
              <Typography sx={{ fontSize: 28, fontWeight: 800, color: isDark ? "#f1f5f9" : "#1e293b", fontFamily: "Poppins" }}>
                {formatNumber(totalValue)}
                <span style={{ fontSize: 14, fontWeight: 600, marginLeft: 2 }}>t</span>
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{
                border: `1px solid ${isDark ? "#3f3f46" : "#f1f5f9"}`,
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: isDark ? "0 2px 10px rgba(0,0,0,0.3)" : "0 2px 10px rgba(0,0,0,0.02)",
              }}>
              {/* Table Header */}
              <Box sx={{ display: "flex", bgcolor: isDark ? "#3f3f46" : "#f8fafc", p: 2, borderBottom: `1px solid ${isDark ? "#52525b" : "#f1f5f9"}` }}>
                <Typography sx={{ ...muiStyle.getTableTitle(isDark), fontSize: 14, flex: 2, color: isDark ? "#4ade80" : "#64748b" }}>Material Type</Typography>
                <Typography sx={{ ...muiStyle.getTableTitle(isDark), fontSize: 14, flex: 1, textAlign: "right", color: isDark ? "#4ade80" : "#64748b" }}>
                  Total Impact (t)
                </Typography>
                <Typography sx={{ ...muiStyle.getTableTitle(isDark), fontSize: 14, flex: 1, textAlign: "right", color: isDark ? "#4ade80" : "#64748b" }}>Distribution</Typography>
              </Box>
              {/* Table Rows */}
              <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                {dataWithPercent.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      p: 1.8,
                      borderBottom: idx < dataWithPercent.length - 1 ? `1px solid ${isDark ? "#27272a" : "#f1f5f9"}` : "none",
                      alignItems: "center",
                      "&:hover": { bgcolor: isDark ? "#27272a" : "#f8fafc" },
                    }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 2 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: COLORS[idx % COLORS.length] }} />
                      <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 13, color: isDark ? "#f1f5f9" : "#1e293b", fontWeight: 600 }}>{item.label}</Typography>
                    </Box>
                    <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 13, flex: 1, textAlign: "right", fontWeight: 700, color: "#16a34a" }}>
                      {formatNumber(item.value)}
                    </Typography>
                    <Typography sx={{ fontFamily: "Poppins", fontSize: 13, flex: 1, textAlign: "right", color: "#64748b", fontWeight: 700 }}>
                      {(item.percent * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

// ============================================================================
// PORTFOLIO BY CATEGORY COMPONENT
// ============================================================================

const PortfolioByCategory = ({ categoryData }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [viewMode, setViewMode] = useState("chart");

  const totalBuildings = useMemo(() => categoryData.reduce((sum, d) => sum + d.count, 0), [categoryData]);
  const totalGwp = useMemo(() => categoryData.reduce((sum, d) => sum + d.value, 0), [categoryData]);

  const dataWithPercent = useMemo(
    () =>
      categoryData.map((d) => ({
        ...d,
        gwpPercent: totalGwp > 0 ? d.value / totalGwp : 0,
        countPercent: totalBuildings > 0 ? d.count / totalBuildings : 0,
      })),
    [categoryData, totalGwp, totalBuildings]
  );

  return (
    <Paper elevation={0} sx={{ p: 3, mb: 3, border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`, borderRadius: 4, bgcolor: isDark ? "#1c1c1e" : "white" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
        <Box>
          <Typography sx={{ ...muiStyle.getTableTitle(isDark), fontSize: 18, color: isDark ? "#4ade80" : muiStyle.typographyTableTitle.color }}>
            {t("Portfolio by Building Category")}
          </Typography>
          <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 13, color: isDark ? "#94a3b8" : undefined }}>
            {t("Distribution of buildings and emissions by type")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => setViewMode("chart")}
            sx={{
              bgcolor: viewMode === "chart" ? "#16a34a" : isDark ? "#27272a" : "transparent",
              color: viewMode === "chart" ? "white" : isDark ? "#e4e4e7" : "#94a3b8",
              border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`,
              borderRadius: "8px",
              "&:hover": { bgcolor: viewMode === "chart" ? "#15803d" : isDark ? "#3f3f46" : "#f8fafc" },
            }}>
            <LuActivity size={16} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setViewMode("table")}
            sx={{
              bgcolor: viewMode === "table" ? "#16a34a" : isDark ? "#27272a" : "transparent",
              color: viewMode === "table" ? "white" : isDark ? "#e4e4e7" : "#94a3b8",
              border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`,
              borderRadius: "8px",
              "&:hover": { bgcolor: viewMode === "table" ? "#15803d" : isDark ? "#3f3f46" : "#f8fafc" },
            }}>
            <LuBox size={16} />
          </IconButton>
        </Box>
      </Box>

      {/* Content: Doughnut or Table */}
      <Box sx={{ minHeight: 400 }}>
        {viewMode === "chart" ? (
          <Box sx={{ position: "relative", width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Box sx={{ height: 400, width: "100%" }}>
              <ResponsiveContainer>
                <RePieChart>
                  <Pie data={dataWithPercent} dataKey="value" nameKey="label" cx="50%" cy="50%" innerRadius={100} outerRadius={140} paddingAngle={3}>
                    {dataWithPercent.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="rgba(255,255,255,0.2)" />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => `${formatNumber(value)} t`}
                    contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 8px 30px rgba(0,0,0,0.1)", fontFamily: "Poppins" }}
                  />
                  <Legend verticalAlign="bottom" align="center" iconType="circle" wrapperStyle={{ fontFamily: "Poppins", fontSize: 13, paddingTop: "20px" }} />
                </RePieChart>
              </ResponsiveContainer>
            </Box>
            {/* Center Label for Doughnut */}
            <Box
              sx={{
                position: "absolute",
                top: "41%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                pointerEvents: "none",
              }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#64748b", fontFamily: "Poppins", mb: -0.5 }}>Portfolio</Typography>
              <Typography sx={{ fontSize: 28, fontWeight: 800, color: isDark ? "#f1f5f9" : "#1e293b", fontFamily: "Poppins" }}>
                {totalBuildings}
                <span style={{ fontSize: 14, fontWeight: 600, marginLeft: 4 }}>Bldgs</span>
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{
                border: `1px solid ${isDark ? "#3f3f46" : "#f1f5f9"}`,
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: isDark ? "0 2px 10px rgba(0,0,0,0.3)" : "0 2px 10px rgba(0,0,0,0.02)",
              }}>
              {/* Header */}
              <Box sx={{ display: "flex", bgcolor: isDark ? "#3f3f46" : "#f8fafc", p: 2, borderBottom: `1px solid ${isDark ? "#52525b" : "#f1f5f9"}` }}>
                <Typography sx={{ ...muiStyle.getTableTitle(isDark), fontSize: 14, flex: 2, color: isDark ? "#4ade80" : "#64748b" }}>Category</Typography>
                <Typography sx={{ ...muiStyle.getTableTitle(isDark), fontSize: 14, flex: 1, textAlign: "right", color: isDark ? "#4ade80" : "#64748b" }}>Building Count</Typography>
                <Typography sx={{ ...muiStyle.getTableTitle(isDark), fontSize: 14, flex: 1.2, textAlign: "right", color: isDark ? "#4ade80" : "#64748b" }}>
                  Total Impact (t)
                </Typography>
              </Box>
              {/* Rows */}
              <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                {dataWithPercent.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      p: 1.8,
                      borderBottom: idx < dataWithPercent.length - 1 ? `1px solid ${isDark ? "#27272a" : "#f1f5f9"}` : "none",
                      alignItems: "center",
                      "&:hover": { bgcolor: isDark ? "#27272a" : "#f8fafc" },
                    }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 2 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: COLORS[idx % COLORS.length] }} />
                      <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 13, color: isDark ? "#f1f5f9" : "#1e293b", fontWeight: 600 }}>{item.label}</Typography>
                    </Box>
                    <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 13, flex: 1, textAlign: "right", fontWeight: 700 }}>{item.count}</Typography>
                    <Typography sx={{ ...muiStyle.typographyTableTitle, fontSize: 13, flex: 1.2, textAlign: "right", color: "#16a34a", fontWeight: 800 }}>
                      {formatNumber(item.value)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

// ============================================================================
// GWP BY BUILDING CHART
// ============================================================================

const GwpByBuildingChart = ({ skylineData }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Paper elevation={0} sx={{ p: 3, mb: 3, border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`, borderRadius: 4, bgcolor: isDark ? "#1c1c1e" : "white" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography sx={{ ...muiStyle.getTableTitle(isDark), fontSize: 18, color: isDark ? "#4ade80" : muiStyle.typographyTableTitle.color }}>{t("GWP by Building")}</Typography>
          <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 13, color: isDark ? "#94a3b8" : undefined }}>
            {t("Comparative emissions footprint across your portfolio")}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ height: skylineData.length * 45 + 100, minHeight: 400, maxHeight: 600 }}>
        <ResponsiveContainer>
          <BarChart layout="vertical" data={skylineData} margin={{ top: 20, right: 80, left: 60, bottom: 60 }}>
            <defs>
              <linearGradient id="barGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#16a34a" stopOpacity={0.8} />
                <stop offset="100%" stopColor="#22c55e" stopOpacity={1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#f1f5f9" />
            <XAxis
              type="number"
              hide={false}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 13, fill: isDark ? '#ffffff' : '#333333', fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
              label={{ value: "Emissions (tCO₂e)", position: "bottom", offset: 20, style: { fontFamily: "Poppins, sans-serif", fontSize: 13, fill: isDark ? '#ffffff' : '#333333', fontWeight: 600 } }}
            />
            <YAxis
              dataKey="label"
              type="category"
              width={180}
              tick={{ fontSize: 12, fill: isDark ? '#ffffff' : '#333333', fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
              axisLine={false}
              tickLine={false}
              label={{ value: "Buildings", angle: -90, position: "insideLeft", offset: -50, style: { fontFamily: "Poppins, sans-serif", fontSize: 13, fill: isDark ? '#ffffff' : '#333333', fontWeight: 600 } }}
            />
            <Tooltip
              cursor={{ fill: "#f8fafc" }}
              contentStyle={{
                borderRadius: 12,
                border: "none",
                boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
                fontFamily: "Poppins",
                padding: "12px",
              }}
              formatter={(value) => [`${formatNumber(value)} tCO₂e`, "Emissions"]}
            />
            <Bar
              dataKey="value"
              fill="url(#barGradient)"
              radius={[0, 6, 6, 0]}
              barSize={24}
              label={{
                position: "right",
                offset: 10,
                fill: isDark ? "#f1f5f9" : "#1e293b",
                fontSize: 12,
                fontWeight: 700,
                fontFamily: "Poppins",
                formatter: (val) => `${formatNumber(val)} t`,
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

// ============================================================================
// TOP 10 EMITTERS TABLE
// ============================================================================

const Top10EmittersTable = ({ buildings }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const top10 = useMemo(() => [...buildings].sort((a, b) => (b.gwp || 0) - (a.gwp || 0)).slice(0, 10), [buildings]);

  const maxGwp = top10[0] ? top10[0].gwp / 1000 : 1;

  return (
    <Paper elevation={0} sx={{ p: 3, mb: 3, border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`, borderRadius: 4, bgcolor: isDark ? "#1c1c1e" : "white" }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ ...muiStyle.getTableTitle(isDark), fontSize: 18, color: isDark ? "#4ade80" : muiStyle.typographyTableTitle.color }}>{t("Top 10 Emitters")}</Typography>
        <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 13, color: isDark ? "#94a3b8" : undefined }}>
          {t("Buildings with highest carbon footprint in your portfolio")}
        </Typography>
      </Box>

      <Box
        sx={{
          border: `1px solid ${isDark ? "#3f3f46" : "#f1f5f9"}`,
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: isDark ? "0 2px 10px rgba(0,0,0,0.3)" : "0 2px 10px rgba(0,0,0,0.02)",
        }}>
        {/* Header */}
        <Box sx={{ display: "flex", bgcolor: isDark ? "#3f3f46" : "#f8fafc", p: 2, borderBottom: `1px solid ${isDark ? "#52525b" : "#f1f5f9"}` }}>
          <Typography sx={{ ...muiStyle.getTableTitle(isDark), fontSize: 12, flex: 0.4, color: isDark ? "#4ade80" : "#64748b" }}>#</Typography>
          <Typography sx={{ ...muiStyle.getTableTitle(isDark), fontSize: 12, flex: 2.5, color: isDark ? "#4ade80" : "#64748b" }}>Building</Typography>
          <Typography sx={{ ...muiStyle.getTableTitle(isDark), fontSize: 12, flex: 1.5, color: isDark ? "#4ade80" : "#64748b" }}>Category</Typography>
          <Typography sx={{ ...muiStyle.getTableTitle(isDark), fontSize: 12, flex: 2, textAlign: "right", color: isDark ? "#4ade80" : "#64748b" }}>
            Impact Intensity (tCO₂e)
          </Typography>
        </Box>
        {/* Rows */}
        {top10.map((building, idx) => {
          const gwpVal = (building.gwp || 0) / 1000;
          const relativeWidth = (gwpVal / maxGwp) * 100;

          return (
            <Box
              key={building.id}
              sx={{
                display: "flex",
                p: 2,
                borderBottom: idx < top10.length - 1 ? `1px solid ${isDark ? "#27272a" : "#f8fafc"}` : "none",
                alignItems: "center",
                transition: "bgcolor 0.2s ease",
                "&:hover": { bgcolor: isDark ? "#27272a" : "#f8fafc" },
              }}>
              <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 13, fontWeight: 700, flex: 0.4, color: idx < 3 ? "#16a34a" : "#94a3b8" }}>{idx + 1}</Typography>
              <Box sx={{ flex: 2.5 }}>
                <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 13, fontWeight: 600, color: isDark ? "#f1f5f9" : "#1e293b" }}>{building.name}</Typography>
              </Box>
              <Box sx={{ flex: 1.5 }}>
                <Box
                  sx={{
                    display: "inline-block",
                    px: 1.5,
                    py: 0.4,
                    borderRadius: 5,
                    bgcolor: isDark ? "#27272a" : "#f1f5f9",
                    color: isDark ? "#94a3b8" : "#475569",
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: "Poppins",
                    border: isDark ? "1px solid #3f3f46" : "none",
                  }}>
                  {building.building_category || "N/A"}
                </Box>
              </Box>
              <Box sx={{ flex: 2, textAlign: "right" }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
                  <Typography sx={{ ...muiStyle.getTableTitle(isDark), fontSize: 14, color: "#16a34a", fontWeight: 800 }}>{formatNumber(gwpVal)}</Typography>
                  <Box sx={{ width: "100%", maxWidth: 120, height: 6, bgcolor: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}>
                    <Box
                      sx={{
                        width: `${relativeWidth}%`,
                        height: "100%",
                        bgcolor: "#16a34a",
                        borderRadius: 4,
                        boxShadow: "0 0 8px rgba(22, 163, 74, 0.2)",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const BuildingSkyline = ({ buildings = [], aggregatedData, loading }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleNext = () => setCurrentIndex((prev) => (prev + 1) % (buildings.length + 1));
  const handlePrev = () => setCurrentIndex((prev) => (prev - 1 + buildings.length + 1) % (buildings.length + 1));

  // Prepare data for components
  const stageData = useMemo(() => {
    const stages = aggregatedData.stages || {};
    return [
      { title: "A1-A3", subtitle: "Manufacturing", value: (stages["A1-A3"] || 0) / 1000, color: "#16a34a" },
      { title: "A4-A5", subtitle: "Construction", value: (stages["A4-A5"] || 0) / 1000, color: "#3b82f6" },
      { title: "B1-B5", subtitle: "Maintenance", value: (stages["B1-B5"] || 0) / 1000, color: "#f59e0b" },
      { title: "B6-B7", subtitle: "Operational", value: (stages["B6-B7"] || 0) / 1000, color: "#eab308" },
      { title: "C", subtitle: "End of Life", value: (stages["C"] || 0) / 1000, color: "#ef4444" },
      { title: "D", subtitle: "Benefits", value: (stages["D"] || 0) / 1000, color: "#8b5cf6" },
    ];
  }, [aggregatedData]);

  // GWP by Building Data
  const skylineData = useMemo(
    () =>
      buildings
        .map((b) => ({
          label: b.name || "Unknown",
          value: (b.gwp || 0) / 1000,
        }))
        .sort((a, b) => a.value - b.value),
    [buildings]
  );

  // Material Composition Data
  const materialData = useMemo(() => {
    if (!aggregatedData.types) return [];
    return Object.entries(aggregatedData.types)
      .map(([label, value]) => ({ label, value: value / 1000 }))
      .sort((a, b) => b.value - a.value);
  }, [aggregatedData]);

  // Category Distribution Data
  const categoryData = useMemo(() => {
    const categories = {};
    buildings.forEach((b) => {
      const cat = b.building_category || "Other";
      if (!categories[cat]) categories[cat] = { count: 0, gwp: 0 };
      categories[cat].count++;
      categories[cat].gwp += b.gwp || 0;
    });
    return Object.entries(categories)
      .map(([label, data]) => ({
        label,
        value: data.gwp / 1000,
        count: data.count,
      }))
      .sort((a, b) => b.value - a.value);
  }, [buildings]);

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography sx={muiStyle.typographyPageSubtitle}>Loading...</Typography>
      </Box>
    );
  }

  if (!buildings.length) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography sx={muiStyle.typographyPageSubtitle}>No buildings available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Building Spotlight */}
      <BuildingSpotlight buildings={buildings} aggregatedData={aggregatedData} currentIndex={currentIndex} onNext={handleNext} onPrev={handlePrev} />

      {/* Life Cycle Stages - WITH CHART OPTION */}
      <LifeCycleStages stageData={stageData} />

      {/* Material Composition */}
      <MaterialComposition materialData={materialData} />

      {/* Portfolio by Category */}
      <PortfolioByCategory categoryData={categoryData} />

      {/* GWP by Building Chart */}
      <GwpByBuildingChart skylineData={skylineData} />

      {/* Top 10 Emitters */}
      <Top10EmittersTable buildings={buildings} />
    </Box>
  );
};

export default BuildingSkyline;
